/**
 * Patient Biometrics Form Component
 * Comprehensive biometrics data collection for patient onboarding
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  LinearProgress,
  Divider,
  Stack,
  FormHelperText,
  InputAdornment
} from '@mui/material';
import {
  Height,
  MonitorWeight,
  Favorite,
  Thermostat,
  Air,
  FitnessCenter,
  Restaurant,
  SmokingRooms,
  LocalBar,
  DirectionsRun,
  Bedtime
} from '@mui/icons-material';
import { toast } from 'react-toastify';

// Error handling
import { handleApiError, getFieldErrors, getSuccessMessage } from '../../utils/errorHandler';

// API Service
import { biometricsApi, formatBiometricsData, parseBiometricsData } from '../../services/api/patientComprehensive';

const PatientBiometricsForm = ({ onSaved, onSkip, initialData }) => {
  const [formData, setFormData] = useState({
    heightCm: '',
    weightKg: '',
    bloodPressureSystolic: '',
    bloodPressureDiastolic: '',
    heartRate: '',
    temperature: '',
    respiratoryRate: '',
    waistCircumference: '',
    hipCircumference: '',
    bodyFatPercentage: '',
    muscleMassKg: '',
    allergies: [],
    drugAllergies: [],
    foodAllergies: [],
    smokingStatus: '',
    alcoholConsumption: '',
    exerciseFrequency: '',
    sleepHours: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [biometricsExists, setBiometricsExists] = useState(false);
  const [newAllergy, setNewAllergy] = useState('');
  const [newDrugAllergy, setNewDrugAllergy] = useState('');
  const [newFoodAllergy, setNewFoodAllergy] = useState('');

  // Load existing biometrics on mount
  useEffect(() => {
    loadBiometrics();
  }, []);

  // Pre-fill with initial data if available
  useEffect(() => {
    if (initialData && !biometricsExists) {
      setFormData(prev => ({
        ...prev,
        ...initialData
      }));
    }
  }, [initialData, biometricsExists]);

  const loadBiometrics = async () => {
    try {
      setLoading(true);
      const response = await biometricsApi.getBiometrics();
      
      if (response && response.length > 0) {
        const biometrics = response[0];
        const parsedData = parseBiometricsData(biometrics);
        setFormData(parsedData);
        setBiometricsExists(true);
      }
    } catch (err) {
      console.error('Error loading biometrics:', err);
      // Only show error if it's not a 404 (empty biometrics is expected for new patients)
      if (err.response?.status !== 404) {
        handleApiError(err, 'biometrics_load', toast);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Height validation (50-300 cm)
    if (formData.heightCm && (formData.heightCm < 50 || formData.heightCm > 300)) {
      newErrors.heightCm = 'Height must be between 50 and 300 cm';
    }
    
    // Weight validation (10-500 kg)
    if (formData.weightKg && (formData.weightKg < 10 || formData.weightKg > 500)) {
      newErrors.weightKg = 'Weight must be between 10 and 500 kg';
    }
    
    // Blood pressure validation
    if (formData.bloodPressureSystolic && (formData.bloodPressureSystolic < 70 || formData.bloodPressureSystolic > 300)) {
      newErrors.bloodPressureSystolic = 'Systolic blood pressure must be between 70 and 300 mmHg';
    }
    
    if (formData.bloodPressureDiastolic && (formData.bloodPressureDiastolic < 40 || formData.bloodPressureDiastolic > 200)) {
      newErrors.bloodPressureDiastolic = 'Diastolic blood pressure must be between 40 and 200 mmHg';
    }
    
    // Heart rate validation (30-250 bpm)
    if (formData.heartRate && (formData.heartRate < 30 || formData.heartRate > 250)) {
      newErrors.heartRate = 'Heart rate must be between 30 and 250 bpm';
    }
    
    // Temperature validation (30-45°C)
    if (formData.temperature && (formData.temperature < 30 || formData.temperature > 45)) {
      newErrors.temperature = 'Temperature must be between 30 and 45°C';
    }
    
    // Sleep hours validation (0-24 hours)
    if (formData.sleepHours && (formData.sleepHours < 0 || formData.sleepHours > 24)) {
      newErrors.sleepHours = 'Sleep duration must be between 0 and 24 hours';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      // Show validation errors on form fields
      const errorMessages = Object.values(errors);
      if (errorMessages.length > 0) {
        toast.error(errorMessages[0]);
      }
      return;
    }

    try {
      setSaving(true);
      
      if (biometricsExists) {
        // Update existing biometrics
        await biometricsApi.updateBiometrics(formData.id, formData);
        toast.success(getSuccessMessage('biometrics_updated'));
      } else {
        // Create new biometrics
        await biometricsApi.saveBiometrics(formData);
        setBiometricsExists(true);
        toast.success(getSuccessMessage('biometrics_saved'));
      }
      
      // Redirect to profile page after successful save
      window.location.href = '/patient-dashboard/profile';
      
    } catch (err) {
      console.error('Error saving biometrics:', err);
      
      // Handle backend validation errors
      if (err.response?.data?.details) {
        const fieldErrors = getFieldErrors(err);
        setErrors(fieldErrors);
        
        // Show first error message
        const firstError = Object.values(fieldErrors)[0];
        if (firstError) {
          toast.error(firstError);
        }
      } else {
        // Handle other errors
        handleApiError(err, 'biometrics_save', toast);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    // Redirect to profile page
    window.location.href = '/patient-dashboard/profile';
  };

  const addAllergy = (type) => {
    let newItem = '';
    let field = '';
    
    switch (type) {
      case 'allergy':
        newItem = newAllergy.trim();
        field = 'allergies';
        setNewAllergy('');
        break;
      case 'drug':
        newItem = newDrugAllergy.trim();
        field = 'drugAllergies';
        setNewDrugAllergy('');
        break;
      case 'food':
        newItem = newFoodAllergy.trim();
        field = 'foodAllergies';
        setNewFoodAllergy('');
        break;
      default:
        return;
    }
    
    if (newItem && !formData[field].includes(newItem)) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], newItem]
      }));
    }
  };

  const removeAllergy = (type, index) => {
    let field = '';
    switch (type) {
      case 'allergy':
        field = 'allergies';
        break;
      case 'drug':
        field = 'drugAllergies';
        break;
      case 'food':
        field = 'foodAllergies';
        break;
      default:
        return;
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const calculateBMI = () => {
    if (formData.heightCm && formData.weightKg) {
      const heightM = formData.heightCm / 100;
      const bmi = (formData.weightKg / (heightM * heightM)).toFixed(1);
      return bmi;
    }
    return null;
  };

  const getBMICategory = (bmi) => {
    if (!bmi) return null;
    if (bmi < 18.5) return { category: 'Underweight', color: 'warning' };
    if (bmi < 25) return { category: 'Normal', color: 'success' };
    if (bmi < 30) return { category: 'Overweight', color: 'warning' };
    return { category: 'Obese', color: 'error' };
  };

  if (loading) {
    return (
      <Box p={3}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading biometrics data...
        </Typography>
      </Box>
    );
  }

  const bmi = calculateBMI();
  const bmiCategory = getBMICategory(bmi);

  return (
    <Box p={3} maxWidth={800} mx="auto">
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            <FitnessCenter sx={{ mr: 1, verticalAlign: 'middle' }} />
            Biometrics & Health Data
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please provide your basic health measurements and lifestyle information. This helps us provide better care.
          </Typography>

          <Grid container spacing={3}>
            {/* Height and Weight */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Measurements
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Height (cm)"
                type="number"
                value={formData.heightCm}
                onChange={(e) => handleInputChange('heightCm', parseFloat(e.target.value) || '')}
                error={!!errors.heightCm}
                helperText={errors.heightCm}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Height /></InputAdornment>,
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Weight (kg)"
                type="number"
                value={formData.weightKg}
                onChange={(e) => handleInputChange('weightKg', parseFloat(e.target.value) || '')}
                error={!!errors.weightKg}
                helperText={errors.weightKg}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><MonitorWeight /></InputAdornment>,
                }}
              />
            </Grid>

            {/* BMI Display */}
            {bmi && (
              <Grid item xs={12}>
                <Alert severity={bmiCategory?.color || 'info'} sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>BMI: {bmi}</strong> - {bmiCategory?.category}
                  </Typography>
                </Alert>
              </Grid>
            )}

            {/* Vital Signs */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Vital Signs
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Systolic BP (mmHg)"
                type="number"
                value={formData.bloodPressureSystolic}
                onChange={(e) => handleInputChange('bloodPressureSystolic', parseInt(e.target.value) || '')}
                error={!!errors.bloodPressureSystolic}
                helperText={errors.bloodPressureSystolic}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Favorite /></InputAdornment>,
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Diastolic BP (mmHg)"
                type="number"
                value={formData.bloodPressureDiastolic}
                onChange={(e) => handleInputChange('bloodPressureDiastolic', parseInt(e.target.value) || '')}
                error={!!errors.bloodPressureDiastolic}
                helperText={errors.bloodPressureDiastolic}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Heart Rate (bpm)"
                type="number"
                value={formData.heartRate}
                onChange={(e) => handleInputChange('heartRate', parseInt(e.target.value) || '')}
                error={!!errors.heartRate}
                helperText={errors.heartRate}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Favorite /></InputAdornment>,
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Temperature (°C)"
                type="number"
                value={formData.temperature}
                onChange={(e) => handleInputChange('temperature', parseFloat(e.target.value) || '')}
                error={!!errors.temperature}
                helperText={errors.temperature}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Thermostat /></InputAdornment>,
                }}
              />
            </Grid>

            {/* Additional Measurements */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Additional Measurements
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Respiratory Rate (breaths/min)"
                type="number"
                value={formData.respiratoryRate}
                onChange={(e) => handleInputChange('respiratoryRate', parseInt(e.target.value) || '')}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Air /></InputAdornment>,
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Waist Circumference (cm)"
                type="number"
                value={formData.waistCircumference}
                onChange={(e) => handleInputChange('waistCircumference', parseFloat(e.target.value) || '')}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Hip Circumference (cm)"
                type="number"
                value={formData.hipCircumference}
                onChange={(e) => handleInputChange('hipCircumference', parseFloat(e.target.value) || '')}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Body Fat %"
                type="number"
                value={formData.bodyFatPercentage}
                onChange={(e) => handleInputChange('bodyFatPercentage', parseFloat(e.target.value) || '')}
              />
            </Grid>

            {/* Allergies */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Allergies & Sensitivities
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  General Allergies
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Add allergy"
                    value={newAllergy}
                    onChange={(e) => setNewAllergy(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addAllergy('allergy')}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => addAllergy('allergy')}
                  >
                    Add
                  </Button>
                </Stack>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {formData.allergies.map((allergy, index) => (
                    <Chip
                      key={index}
                      label={allergy}
                      onDelete={() => removeAllergy('allergy', index)}
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Drug Allergies
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Add drug allergy"
                    value={newDrugAllergy}
                    onChange={(e) => setNewDrugAllergy(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addAllergy('drug')}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => addAllergy('drug')}
                  >
                    Add
                  </Button>
                </Stack>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {formData.drugAllergies.map((allergy, index) => (
                    <Chip
                      key={index}
                      label={allergy}
                      onDelete={() => removeAllergy('drug', index)}
                      size="small"
                      color="error"
                    />
                  ))}
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Food Allergies
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Add food allergy"
                    value={newFoodAllergy}
                    onChange={(e) => setNewFoodAllergy(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addAllergy('food')}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => addAllergy('food')}
                  >
                    Add
                  </Button>
                </Stack>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {formData.foodAllergies.map((allergy, index) => (
                    <Chip
                      key={index}
                      label={allergy}
                      onDelete={() => removeAllergy('food', index)}
                      size="small"
                      color="warning"
                    />
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Lifestyle */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Lifestyle Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Smoking Status</InputLabel>
                <Select
                  value={formData.smokingStatus}
                  onChange={(e) => handleInputChange('smokingStatus', e.target.value)}
                  startAdornment={<SmokingRooms />}
                >
                  <MenuItem value="never">Never Smoked</MenuItem>
                  <MenuItem value="former">Former Smoker</MenuItem>
                  <MenuItem value="current">Current Smoker</MenuItem>
                  <MenuItem value="passive">Passive Smoker</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Alcohol Consumption</InputLabel>
                <Select
                  value={formData.alcoholConsumption}
                  onChange={(e) => handleInputChange('alcoholConsumption', e.target.value)}
                  startAdornment={<LocalBar />}
                >
                  <MenuItem value="never">Never</MenuItem>
                  <MenuItem value="occasional">Occasional</MenuItem>
                  <MenuItem value="moderate">Moderate</MenuItem>
                  <MenuItem value="heavy">Heavy</MenuItem>
                  <MenuItem value="former">Former Heavy Drinker</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Exercise Frequency</InputLabel>
                <Select
                  value={formData.exerciseFrequency}
                  onChange={(e) => handleInputChange('exerciseFrequency', e.target.value)}
                  startAdornment={<DirectionsRun />}
                >
                  <MenuItem value="never">Never</MenuItem>
                  <MenuItem value="rarely">Rarely</MenuItem>
                  <MenuItem value="sometimes">Sometimes</MenuItem>
                  <MenuItem value="regular">Regular</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sleep Hours (per night)"
                type="number"
                value={formData.sleepHours}
                onChange={(e) => handleInputChange('sleepHours', parseFloat(e.target.value) || '')}
                error={!!errors.sleepHours}
                helperText={errors.sleepHours}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Bedtime /></InputAdornment>,
                }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleSkip}
              disabled={saving}
            >
              Skip for Now
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Biometrics'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PatientBiometricsForm;
